import { ChevronDownIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs
} from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Components
import LayoutWrapper from "../../shared/layout";
import UsersIndexTable from "./index/UsersIndexTable";

// Hooks
import useIndex from "../../hooks/useIndex";
import { UserTabKey, useUsers } from "../../hooks/useUsers";
import useClaimsToast from "../../hooks/useToast";

// Utils
import { IParams, getCacheKeyObject } from "../../shared/DataTable/hooks/useSearchParams";
import { postRequestPasswordReset, postResendWelcome } from "../../utils/api/auth";
import { CustomColumnFilterType, DataTableColumn, KVP, SortDirection } from "../../utils/types";

// Constants
import { ROLE_OPTIONS, STATUS_OPTIONS, TAB_LABELS, USERS_TABS } from "./constants";
import usePermission from "../../hooks/usePermission";

// Constants
const DEFAULT_PARAMS: Partial<IParams> = {
	sortDirection: SortDirection.Asc,
	sortField: "email"
};

// Define filter state interface
interface UserFilters {
	page: number;
	pageSize: number;
	sortBy: string;
	sortOrder: string;
	emailFilter?: string;
	nameFilter?: string;
	externalIdFilter?: string;
	rolesList?: string[];
	status?: string;
}

/**
 * Define tabs on the page
 */
enum UsersTabs {
	LegalEase,
	Providers
}

/**
 * Handles the Users listing page
 * Shows different data depending on which tab is active
 * Each individual tab is rendered as a child component
 */
const UsersPage: FunctionComponent = (): ReactElement => {
	// Custom hook for users data management
	const { isLoading, fetchUsers, getUsersData, setIsLoading } = useUsers();
	const { hasGeneralAdminAccess, hasGeneralManagerAccess, hasGeneralNetworkAccess } = usePermission();
	const toast = useClaimsToast();

	// Filter state for each tab
	const [filters, setFilters] = useState<Record<UserTabKey, UserFilters>>({
		[USERS_TABS.LegalEase]: {
			page: 1,
			pageSize: 20,
			sortBy: "email",
			sortOrder: "asc"
		},
		[USERS_TABS.Providers]: {
			page: 1,
			pageSize: 20,
			sortBy: "email",
			sortOrder: "asc"
		}
	});

	// Hooks
	const { activeTab, setActiveTab, getTabLabel } = useIndex({
		tabEnum: UsersTabs,
		tabCacheKeys: ["", ""] // Not used anymore
	});
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	// Get current tab key
	const currentTabKey = useMemo((): UserTabKey => {
		return activeTab === UsersTabs.LegalEase ? USERS_TABS.LegalEase : USERS_TABS.Providers;
	}, [activeTab]);

	/**
	 * Handles switching between active tabs
	 */
	const handleChangedTab = (index: number): void => {
		setActiveTab(index);
		navigate(`/users/${getTabLabel(index)}`);
	};

	/**
	 * Handles filter changes from the DataTable
	 */
	const handleFilterChange = (cacheKey: string): void => {
		// Parse the cacheKey into IParams object
		const parsedParams: IParams = getCacheKeyObject(cacheKey);

		// Map DataTable filters to our filter format
		const mappedFilters: UserFilters = {
			page: parsedParams.page || 1,
			pageSize: parsedParams.limit || 20,
			sortBy: parsedParams.sortField || "email",
			sortOrder: parsedParams.sortDirection?.toLowerCase() || "asc",
			emailFilter: parsedParams.model?.email ? String(parsedParams.model.email) : undefined,
			nameFilter: parsedParams.model?.name ? String(parsedParams.model.name) : undefined,
			externalIdFilter: parsedParams.model?.externalId ? String(parsedParams.model.externalId) : undefined,
			rolesList: parsedParams.model?.roles ? [String(parsedParams.model.roles)] : undefined,
			status: parsedParams.model?.status ? String(parsedParams.model.status) : undefined
		};
		// Update filters for current tab
		setFilters((prev) => ({
			...prev,
			[currentTabKey]: mappedFilters
		}));

		// Fetch data with new filters
		fetchUsers(currentTabKey, mappedFilters);
	};

	const toggleLoading = (isPageLoading: boolean): void => setIsLoading(isPageLoading);

	const requestPasswordReset = async (email: string): Promise<void> => {
		setIsLoading(true);

		postRequestPasswordReset(email)
			.then(() => {
				toast("Success", "A password reset email was successfully sent", "success");
			})
			.catch((err) => {
				console.error(err);
				toast("Error", "There was a problem sending the password reset email", "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const resendWelcome = async (id: number): Promise<void> => {
		setIsLoading(true);

		postResendWelcome(id)
			.then(() => {
				toast("Success", "A welcome email was successfully sent", "success");
			})
			.catch((err) => {
				console.error(err);
				toast("Error", "There was a problem sending the welcome email", "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	// Initial data fetch on component mount
	useEffect(() => {
		// Fetch initial data for the current tab
		fetchUsers(currentTabKey, filters[currentTabKey]);
	}, [currentTabKey]); // Re-fetch when tab changes

	const internalColumns: Array<DataTableColumn> = [
		{
			header: "Email",
			id: "email",
			filterable: true,
			minWidth: "25ch",
			isHidden: false,
			isSelectable: true,
			isSortable: true,
			allData: true,
			valueMap: (user: KVP) => (
				<Button
					as={Link}
					to={`/users/user/${user["id"]}`}
					state={{ userData: user }}
					variant="link"
					color="orange.600"
					fontWeight="normal"
					size="sm"
					_hover={{ textDecoration: "underline" }}>
					{user["email"]}
				</Button>
			)
		},
		{
			header: "Roles",
			id: "roles",
			filterable: true,
			minWidth: "15ch",
			isHidden: false,
			isSortable: true,
			customFilter: {
				type: CustomColumnFilterType.Select,
				options: ROLE_OPTIONS
			}
		},
		{
			header: "Status",
			id: "status",
			filterable: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true,
			customFilter: {
				type: CustomColumnFilterType.Select,
				options: STATUS_OPTIONS
			}
		},
		{
			header: "Created",
			id: "createdAt",
			filterable: false,
			minWidth: "12ch",
			isHidden: false,
			isSortable: true,
			isDate: true
		}
	];

	const externalColumns: Array<DataTableColumn> = [
		{
			header: "External Id",
			id: "externalId",
			filterable: true,
			minWidth: "10ch",
			maxWidth: "10ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Email",
			id: "email",
			filterable: true,
			minWidth: "25ch",
			isHidden: false,
			isSelectable: true,
			isSortable: true,
			allData: true,
			valueMap: (user: KVP) => (
				<Button
					as={Link}
					to={`/users/user/${user["id"]}`}
					state={{ userData: user }}
					variant="link"
					color="orange.600"
					fontWeight="normal"
					size="sm"
					_hover={{ textDecoration: "underline" }}>
					{user["email"]}
				</Button>
			)
		},
		{
			header: "Name",
			id: "name",
			filterable: true,
			minWidth: "15ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Status",
			id: "status",
			filterable: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true,
			customFilter: {
				type: CustomColumnFilterType.Select,
				options: STATUS_OPTIONS
			}
		},
		{
			header: "Confirmed",
			id: "confirmedAt",
			filterable: false,
			minWidth: "12ch",
			isHidden: false,
			isSortable: true,
			isDate: true
		},
		{
			header: "Created",
			id: "createdAt",
			filterable: false,
			minWidth: "12ch",
			isHidden: false,
			isSortable: true,
			isDate: true
		},
		{
			header: "",
			id: "action",
			filterable: false,
			minWidth: "15ch",
			isHidden: !(hasGeneralAdminAccess || hasGeneralNetworkAccess),
			isSortable: false,
			allData: true,
			valueMap: (user: KVP) => {
				const actions = [];
				const hasNetworkAccess = hasGeneralAdminAccess || hasGeneralNetworkAccess;
				if (user["status"] === "Pending" && user["id"] && hasNetworkAccess) {
					actions.push(<MenuItem onClick={() => resendWelcome(+user["id"])}>Resend Welcome Email</MenuItem>);
				}
				if (user["status"] === "Active" && user["email"] && hasNetworkAccess) {
					actions.push(
						<MenuItem onClick={() => requestPasswordReset(user["email"].toString())}>
							Send Password Reset Email
						</MenuItem>
					);
				}
				return actions.length ? (
					<Menu>
						<MenuButton
							as={Button}
							px={4}
							py={2}
							size="sm"
							fontSize="14px"
							colorScheme="blackAlpha"
							transition="all 0.2s"
							borderRadius="md"
							borderWidth="1px"
							borderColor="gray.400"
							outline="none"
							_hover={{ bg: "gray.600" }}
							_expanded={{ bg: "gray.500" }}
							_focus={{ boxShadow: "outline" }}>
							Actions
							<ChevronDownIcon fontSize="18px" />
						</MenuButton>
						<MenuList borderColor="gray.300">{actions}</MenuList>
					</Menu>
				) : (
					<></>
				);
			}
		}
	];

	return (
		<>
			<LayoutWrapper>
				<Box data-testid="users-wrapper">
					<Tabs
						colorScheme="orange"
						variant="enclosed-colored"
						margin="20px 10px"
						isLazy={true}
						index={activeTab}
						onChange={handleChangedTab}>
						<Box display="flex" borderColor="transparent" marginBottom="1px" marginLeft="1px">
							<TabList marginLeft="15px" position="relative">
								<Tab
									borderTopWidth="0px"
									backgroundColor="gray.200"
									fontWeight="bold"
									fontSize="19px"
									_selected={{ bg: "white" }}>
									{TAB_LABELS.LegalEase}
								</Tab>
								<Tab
									borderTopWidth="0px"
									backgroundColor="gray.200"
									fontWeight="bold"
									fontSize="19px"
									marginLeft="4px"
									_selected={{ bg: "white" }}>
									{TAB_LABELS.Providers}
								</Tab>
								{/* Filler background tab */}
								<Tab
									position="absolute"
									backgroundColor="#e9e9e9"
									zIndex="-1"
									left="0"
									right="0"
									bottom="0"
									top="0"
									boxShadow="1px 2px 9px 1px gainsboro"
								/>
							</TabList>
						</Box>
						<TabPanels>
							<TabPanel pt="0">
								{activeTab === UsersTabs.LegalEase && (
									<UsersIndexTable
										tab={USERS_TABS.LegalEase}
										usersData={getUsersData(USERS_TABS.LegalEase)}
										columns={internalColumns}
										searchParams={searchParams}
										handleFilterChange={handleFilterChange}
										handleToggleLoading={toggleLoading}
										defaultParams={DEFAULT_PARAMS}
										isLoading={isLoading}
									/>
								)}
							</TabPanel>
							<TabPanel pt="0">
								{activeTab === UsersTabs.Providers && (
									<UsersIndexTable
										tab={USERS_TABS.Providers}
										usersData={getUsersData(USERS_TABS.Providers)}
										columns={externalColumns}
										searchParams={searchParams}
										handleFilterChange={handleFilterChange}
										handleToggleLoading={toggleLoading}
										defaultParams={DEFAULT_PARAMS}
										isLoading={isLoading}
									/>
								)}
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default UsersPage;
